import React, { useState } from 'react';
import TabelaCrud from '../components/TabelaCrud';
import { useCreches } from '../hooks/useCreches';
import { useTurmas } from '../hooks/useTurmas';
import { formatCpf, isValidCpf, normalizeCep } from '../common/validators';
import api, { getCurrentTenantId } from '../services/api';
import HistoricoCrianca from './HistoricoCrianca';
import HistoricoCampo from './HistoricoCampo';
import BlocoResponsavelAluno from './BlocoResponsavelAluno';

interface Aluno {
  id: number;
  creche_id: number;
  nome: string;
  sexo: string;
  data_nascimento: string;
  cpf: string;
  rg: string;
  foto_url: string; // foto da criança (data URI ou URL)
  tipo_sanguineo: string;
  alergias: string;
  restricoes_alimentares: string;
  medicamentos_uso: string;
  contato_medico: string;
  hospital_referencia: string;
  historico_fisico: string;
  historico_cognitivo: string;
  historico_socioemocional: string;
  calendario_vacinal: string;
  rotina_alimentacao_base: string;
  rotina_sono_base: string;
  rotina_atividades_base: string;
  diario_fotos: string;
  diario_observacoes: string;
  responsavel: string;
  telefone_responsavel: string;
  responsavel_email: string;
  turma_id: number;
  // SaaS Lifecycle para Alunos
  status: 'LEAD' | 'FILA_ESPERA' | 'MATRICULADO' | 'CANCELADO';
  contrato_assinado: boolean;
  data_matricula: string;
  // Dados detalhados do responsável para formalização (persistidos no Aluno)
  responsavel_cpf: string;
  responsavel_rg: string;
  responsavel_profissao: string;
  responsavel_nacionalidade: string;
  responsavel_estado_civil: string;
  responsavel_endereco: string; // composto automaticamente a partir dos campos abaixo
  responsavel_cep: string;
  responsavel_logradouro: string;
  responsavel_numero: string;
  responsavel_complemento: string;
  responsavel_bairro: string;
  responsavel_cidade: string;
  responsavel_estado: string;
  responsavel_parentesco: string;
}

const LIMITE_IMAGEM = 800 * 1024; // 800 KB por imagem
const MAX_FOTOS_DIARIO = 12;

/** Lê um arquivo de imagem e devolve um data URI (base64). Valida tipo e tamanho. */
function lerImagem(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) return reject(new Error('Selecione um arquivo de imagem (PNG, JPG, WEBP).'));
    if (file.size > LIMITE_IMAGEM) return reject(new Error('Imagem muito grande (máximo 800 KB por foto).'));
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Não foi possível ler a imagem.'));
    reader.readAsDataURL(file);
  });
}

/** diario_fotos é um JSON array de imagens; aceita também valor legado (uma URL em string). */
function parseFotos(raw?: string): string[] {
  if (!raw) return [];
  try {
    const v = JSON.parse(raw);
    if (Array.isArray(v)) return v.filter((x) => typeof x === 'string' && x.trim());
  } catch { /* valor legado (string simples) */ }
  return String(raw).trim() ? [String(raw).trim()] : [];
}

function serializeFotos(arr: string[]): string {
  const limpas = arr.filter(Boolean);
  return limpas.length ? JSON.stringify(limpas) : '';
}

function ehDataUri(v?: string): boolean {
  return !!v && v.startsWith('data:');
}

export default function AlunosPage() {
  const { options, creches } = useCreches();
  const { turmas } = useTurmas();
  // Alunos são sempre por-creche: o backend força id_creche = tenant logado. Com tenant
  // ativo, a creche é implícita e o seletor fica oculto; sem tenant (admin de sistema),
  // o seletor aparece para escolher a creche.
  const tenantScoped = Boolean(getCurrentTenantId());
  const [gerandoContrato, setGerandoContrato] = useState(false);
  const [contratoMsg, setContratoMsg] = useState<{ tipo: 'ok' | 'erro'; texto: string } | null>(null);
  const [buscandoCepResp, setBuscandoCepResp] = useState(false);
  const [imgErro, setImgErro] = useState('');
  const [urlDiario, setUrlDiario] = useState('');

  type OnChange = (campo: keyof Aluno, valor: any) => void;

  async function onFotoCrianca(e: React.ChangeEvent<HTMLInputElement>, onChange: OnChange) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await lerImagem(file);
      onChange('foto_url', dataUrl);
      setImgErro('');
    } catch (err: any) {
      setImgErro(err?.message || 'Falha ao carregar a imagem.');
    } finally {
      e.target.value = '';
    }
  }

  async function onFotosDiario(e: React.ChangeEvent<HTMLInputElement>, item: Partial<Aluno>, onChange: OnChange) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const atuais = parseFotos(item.diario_fotos);
    try {
      const novas: string[] = [];
      for (const f of files) novas.push(await lerImagem(f));
      const total = [...atuais, ...novas].slice(0, MAX_FOTOS_DIARIO);
      onChange('diario_fotos', serializeFotos(total));
      setImgErro('');
    } catch (err: any) {
      setImgErro(err?.message || 'Falha ao carregar as imagens.');
    } finally {
      e.target.value = '';
    }
  }

  function removerFotoDiario(index: number, item: Partial<Aluno>, onChange: OnChange) {
    const atuais = parseFotos(item.diario_fotos);
    onChange('diario_fotos', serializeFotos(atuais.filter((_, i) => i !== index)));
  }

  function adicionarUrlDiario(item: Partial<Aluno>, onChange: OnChange) {
    const u = urlDiario.trim();
    if (!u) return;
    const atuais = parseFotos(item.diario_fotos);
    onChange('diario_fotos', serializeFotos([...atuais, u].slice(0, MAX_FOTOS_DIARIO)));
    setUrlDiario('');
  }

  async function buscarCepResponsavel(
    item: Partial<Aluno>,
    onChange: (campo: keyof Aluno, valor: any) => void,
  ) {
    const cep = normalizeCep(item.responsavel_cep || '');
    if (cep.length !== 8) return;
    setBuscandoCepResp(true);
    try {
      const resp = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await resp.json();
      if (data.erro) {
        alert('CEP não encontrado.');
        return;
      }
      onChange('responsavel_logradouro', data.logradouro || '');
      onChange('responsavel_bairro', data.bairro || '');
      onChange('responsavel_cidade', data.localidade || '');
      onChange('responsavel_estado', data.uf || '');
    } catch {
      alert('Não foi possível consultar o CEP no momento.');
    } finally {
      setBuscandoCepResp(false);
    }
  }

  function nomeCrecheById(id: number) {
    return creches.find((c) => c.id === id)?.nome || `ID ${id}`;
  }

  function nomeTurmaById(id: number) {
    // Criança sem turma é estado legítimo — matriculada e ainda não enturmada.
    // Antes caía no fallback e a coluna exibia "ID null", que parece defeito de
    // cadastro e manda o gestor procurar erro onde não há.
    if (id === null || id === undefined) return 'Sem turma';
    return turmas.find((t) => t.id === id)?.nome || `ID ${id}`;
  }

  async function gerarContrato(item: Partial<Aluno>) {
    if (!item.id) {
      setContratoMsg({ tipo: 'erro', texto: 'Salve os dados da criança (botão Salvar) antes de gerar o contrato.' });
      return;
    }
    setGerandoContrato(true);
    setContratoMsg(null);
    try {
      const { data } = await api.post(`/alunos/${item.id}/gerar-contrato`);
      setContratoMsg({ tipo: 'ok', texto: `"${data.titulo}" gerado com sucesso — vá em Secretaria & Termos para revisar e assinar.` });
    } catch (e: any) {
      const msg = e?.response?.data?.message;
      setContratoMsg({ tipo: 'erro', texto: Array.isArray(msg) ? msg.join(' ') : (msg || 'Erro ao gerar contrato.') });
    } finally {
      setGerandoContrato(false);
    }
  }

  return (
    <TabelaCrud<Aluno>
      titulo="Crianças"
      tituloModal="Cadastro — Crianças"
      endpoint="/alunos"
      modalLargo
      recarregarAoEditar
      colunas={[
        { chave: 'id', label: 'ID' },
        { chave: 'creche_id', label: 'Creche', render: (item) => nomeCrecheById(item.creche_id) },
        { chave: 'nome', label: 'Nome' },
        { chave: 'turma_id', label: 'Turma', render: (item) => nomeTurmaById(item.turma_id) },
        { 
          chave: 'status', 
          label: 'Status',
          render: (item) => {
            const config: any = {
              'LEAD': { color: '#FFA000', label: 'Lead/Interesse' },
              'FILA_ESPERA': { color: '#1976D2', label: 'Fila de Espera' },
              'MATRICULADO': { color: '#388E3C', label: 'Matriculado' },
              'CANCELADO': { color: '#D32F2F', label: 'Cancelado' }
            };
            const s = config[item.status || 'LEAD'];
            return <span style={{ color: s.color, fontWeight: 'bold' }}>● {s.label}</span>;
          }
        },
        { chave: 'tipo_sanguineo', label: 'Saúde' },
      ]}
      itemVazio={{
        creche_id: 0,
        nome: '',
        sexo: '',
        data_nascimento: '',
        cpf: '',
        rg: '',
        foto_url: '',
        tipo_sanguineo: '',
        alergias: '',
        restricoes_alimentares: '',
        medicamentos_uso: '',
        contato_medico: '',
        hospital_referencia: '',
        historico_fisico: '',
        historico_cognitivo: '',
        historico_socioemocional: '',
        calendario_vacinal: '',
        rotina_alimentacao_base: '',
        rotina_sono_base: '',
        rotina_atividades_base: '',
        diario_fotos: '',
        diario_observacoes: '',
        responsavel: '',
        telefone_responsavel: '',
        responsavel_email: '',
        turma_id: 0,
        status: 'LEAD',
        contrato_assinado: false,
        data_matricula: '',
        responsavel_cpf: '',
        responsavel_rg: '',
        responsavel_profissao: '',
        responsavel_nacionalidade: 'Brasileira',
        responsavel_estado_civil: '',
        responsavel_endereco: '',
        responsavel_cep: '',
        responsavel_logradouro: '',
        responsavel_numero: '',
        responsavel_complemento: '',
        responsavel_bairro: '',
        responsavel_cidade: '',
        responsavel_estado: '',
        responsavel_parentesco: '',
      }}
      validateItem={(item) => {
        if (!tenantScoped && (!item.creche_id || Number(item.creche_id) <= 0)) return 'Selecione a Creche da criança.';
        if (!item.nome?.trim()) return 'Nome completo da criança é obrigatório.';
        if (!item.data_nascimento) return 'Data de nascimento é obrigatória.';
        if (item.cpf?.trim() && !isValidCpf(item.cpf)) return 'CPF da criança inválido.';
        return null;
      }}
      renderFormulario={(item, onChange) => (
        <>
          <h4 className="form-secao-titulo">Dados Pessoais</h4>
          <div className="linha-campos linha-2">
            <label className={tenantScoped ? 'campo-tenant-oculto' : undefined}>Creche
              <select value={item.creche_id ?? 0} onChange={(e) => onChange('creche_id', Number(e.target.value))}>
                <option value={0}>Selecione...</option>
                {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <label>Nome completo
              <input value={item.nome ?? ''} onChange={(e) => onChange('nome', e.target.value)} />
            </label>
          </div>
          <div className="linha-campos linha-3">
            <label>Data de Nascimento
              <input type="date" value={item.data_nascimento ?? ''} onChange={(e) => onChange('data_nascimento', e.target.value)} />
            </label>
            <label>Sexo
              <select value={item.sexo ?? ''} onChange={(e) => onChange('sexo', e.target.value)}>
                <option value="">Selecione...</option>
                <option value="feminino">Feminino</option>
                <option value="masculino">Masculino</option>
                <option value="outro">Outro</option>
              </select>
            </label>
            <label>Turma ID
              <select value={item.turma_id ?? 0} onChange={(e) => onChange('turma_id', Number(e.target.value))}>
                <option value={0}>Selecione...</option>
                {turmas.map((turma) => <option key={turma.id} value={turma.id}>{turma.nome}</option>)}
              </select>
            </label>
          </div>
          <div className="linha-campos linha-2">
            <label>CPF (se aplicável)
              <input value={item.cpf ?? ''} onChange={(e) => onChange('cpf', formatCpf(e.target.value))} placeholder="000.000.000-00" />
            </label>
            <label>RG (se aplicável)
              <input value={item.rg ?? ''} onChange={(e) => onChange('rg', e.target.value)} />
            </label>
          </div>

          <label>Foto da Criança
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap', marginTop: 4 }}>
              {item.foto_url ? (
                <div style={{ textAlign: 'center' }}>
                  <img src={item.foto_url} alt="Foto da criança" style={{ width: 110, height: 110, borderRadius: 12, objectFit: 'cover', border: '1px solid #ddd' }} />
                  <div style={{ marginTop: 6, display: 'flex', gap: 8, justifyContent: 'center' }}>
                    <a href={item.foto_url} download={`foto-${(item.nome || 'crianca').replace(/\s+/g, '-')}.jpg`} target="_blank" rel="noreferrer" className="btn-secundario lp-cta-sm">Baixar</a>
                    <button type="button" className="btn-acao btn-perigo" onClick={() => onChange('foto_url', '')}>Remover</button>
                  </div>
                </div>
              ) : (
                <div style={{ width: 110, height: 110, borderRadius: 12, background: '#f1f1f4', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 40 }}>🧒</div>
              )}
              <div style={{ flex: 1, minWidth: 220, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span className="texto-ajuda">Envie do dispositivo (até 800 KB) ou cole uma URL.</span>
                <input type="file" accept="image/*" onChange={(e) => onFotoCrianca(e, onChange)} />
                <input
                  placeholder="https://... (opcional)"
                  value={ehDataUri(item.foto_url) ? '' : (item.foto_url ?? '')}
                  onChange={(e) => onChange('foto_url', e.target.value)}
                />
              </div>
            </div>
          </label>
          {imgErro && <p className="erro-campo">{imgErro}</p>}

          <h4 className="form-secao-titulo">Saúde e Emergência</h4>
          <div className="linha-campos linha-2">
            <label>Tipo sanguíneo
              <input value={item.tipo_sanguineo ?? ''} onChange={(e) => onChange('tipo_sanguineo', e.target.value)} />
            </label>
            <label>Alergias
              <input value={item.alergias ?? ''} onChange={(e) => onChange('alergias', e.target.value)} />
            </label>
          </div>
          <div className="linha-campos linha-2">
            <label>Restrições alimentares
              <input value={item.restricoes_alimentares ?? ''} onChange={(e) => onChange('restricoes_alimentares', e.target.value)} />
            </label>
            <label>Medicamentos em uso
              <input value={item.medicamentos_uso ?? ''} onChange={(e) => onChange('medicamentos_uso', e.target.value)} />
            </label>
          </div>
          <div className="linha-campos linha-2">
            <label>Contato médico
              <input value={item.contato_medico ?? ''} onChange={(e) => onChange('contato_medico', e.target.value)} />
            </label>
            <label>Hospital de referência
              <input value={item.hospital_referencia ?? ''} onChange={(e) => onChange('hospital_referencia', e.target.value)} />
            </label>
          </div>

          <h4 className="form-secao-titulo">Desenvolvimento e Rotina</h4>
          <p style={{ fontSize: 12, color: '#888', margin: '0 0 8px' }}>
            O campo de texto guarda a situação <strong>inicial/atual</strong> (base). Logo abaixo de cada item, use <strong>data + "Novo Histórico"/"Nova Rotina"</strong> para registrar a <strong>evolução ao longo do tempo</strong>.
          </p>
          {!item.id && (
            <p style={{ fontSize: 13, color: '#e65100', background: '#fff8e1', border: '1px solid #ffe0b2', borderRadius: 8, padding: '8px 10px' }}>
              💡 Salve a criança primeiro para liberar os botões de <strong>Novo Histórico / Nova Rotina</strong> (as datas ficam vinculadas à criança).
            </p>
          )}

          <label>Histórico físico
            <textarea rows={2} value={item.historico_fisico ?? ''} onChange={(e) => onChange('historico_fisico', e.target.value)} />
          </label>
          {item.id && <HistoricoCampo alunoId={item.id} categoria="fisico" tipo="historico" />}

          <label>Histórico cognitivo
            <textarea rows={2} value={item.historico_cognitivo ?? ''} onChange={(e) => onChange('historico_cognitivo', e.target.value)} />
          </label>
          {item.id && <HistoricoCampo alunoId={item.id} categoria="cognitivo" tipo="historico" />}

          <label>Histórico socioemocional
            <textarea rows={2} value={item.historico_socioemocional ?? ''} onChange={(e) => onChange('historico_socioemocional', e.target.value)} />
          </label>
          {item.id && <HistoricoCampo alunoId={item.id} categoria="socioemocional" tipo="historico" />}

          <label>Calendário vacinal atualizado
            <textarea rows={2} value={item.calendario_vacinal ?? ''} onChange={(e) => onChange('calendario_vacinal', e.target.value)} />
          </label>
          {item.id && <HistoricoCampo alunoId={item.id} categoria="vacinal" tipo="historico" />}

          <label>Rotina alimentação
            <input value={item.rotina_alimentacao_base ?? ''} onChange={(e) => onChange('rotina_alimentacao_base', e.target.value)} />
          </label>
          {item.id && <HistoricoCampo alunoId={item.id} categoria="rotina_alimentacao" tipo="rotina" />}

          <label>Rotina sono
            <input value={item.rotina_sono_base ?? ''} onChange={(e) => onChange('rotina_sono_base', e.target.value)} />
          </label>
          {item.id && <HistoricoCampo alunoId={item.id} categoria="rotina_sono" tipo="rotina" />}

          <label>Rotina atividades
            <input value={item.rotina_atividades_base ?? ''} onChange={(e) => onChange('rotina_atividades_base', e.target.value)} />
          </label>
          {item.id && <HistoricoCampo alunoId={item.id} categoria="rotina_atividades" tipo="rotina" />}

          {item.id && (
            <>
              <h4 className="form-secao-titulo">Compartilhar histórico</h4>
              <HistoricoCrianca alunoId={item.id} />
            </>
          )}

          <h4 className="form-secao-titulo">Diário de Bordo</h4>
          <label>Fotos do Diário
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
              <span className="texto-ajuda">Envie fotos do dispositivo (até {MAX_FOTOS_DIARIO}, 800 KB cada) ou adicione por URL.</span>
              <input type="file" accept="image/*" multiple onChange={(e) => onFotosDiario(e, item, onChange)} />
              <div style={{ display: 'flex', gap: 8 }}>
                <input placeholder="https://... (opcional)" value={urlDiario} onChange={(e) => setUrlDiario(e.target.value)} style={{ flex: 1 }} />
                <button type="button" className="btn-secundario" onClick={() => adicionarUrlDiario(item, onChange)}>+ Adicionar URL</button>
              </div>
              {parseFotos(item.diario_fotos).length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 4 }}>
                  {parseFotos(item.diario_fotos).map((foto, idx) => (
                    <div key={idx} style={{ textAlign: 'center' }}>
                      <img src={foto} alt={`Foto ${idx + 1} do diário`} style={{ width: 88, height: 88, borderRadius: 8, objectFit: 'cover', border: '1px solid #ddd' }} />
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 4 }}>
                        <a href={foto} download={`diario-${idx + 1}.jpg`} target="_blank" rel="noreferrer" title="Baixar" style={{ textDecoration: 'none', fontSize: 16 }}>⬇️</a>
                        <button type="button" className="btn-acao btn-perigo" title="Remover" onClick={() => removerFotoDiario(idx, item, onChange)}>🗑️</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </label>
          <label>Observações pedagógicas e comportamentais
            <textarea rows={3} value={item.diario_observacoes ?? ''} onChange={(e) => onChange('diario_observacoes', e.target.value)} />
          </label>

          <BlocoResponsavelAluno
            item={item}
            onChange={onChange}
            buscandoCep={buscandoCepResp}
            onBuscarCep={() => buscarCepResponsavel(item, onChange)}
          />

          <h4 className="form-secao-titulo">Matrícula e Contrato (Relação Creche-Família)</h4>
          <p className="alerta-nao-persiste">ℹ️ O <strong>status da matrícula</strong> e a <strong>data de efetivação</strong> (preenchida automaticamente ao efetivar) são geridos em <strong>Demanda &amp; Matrículas</strong> — não por esta tela de cadastro da criança.</p>

          <div style={{ marginTop: '15px', padding: '15px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#fafafa' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
              <div>
                <strong>Contrato de Prestação de Serviços</strong>
                <p className="texto-ajuda">Gera o contrato com os dados da criança, do responsável (incl. endereço) e da creche, pronto para revisão/assinatura em Secretaria &amp; Termos. Salve a criança antes de gerar.</p>
              </div>
              <button
                type="button"
                className="btn-secundario"
                disabled={gerandoContrato}
                onClick={() => gerarContrato(item)}
              >
                {gerandoContrato ? 'Gerando…' : '📄 Gerar Contrato'}
              </button>
            </div>
            {contratoMsg && (
              <p style={{ marginTop: 10, color: contratoMsg.tipo === 'ok' ? '#166534' : '#b91c1c', fontWeight: 600 }}>
                {contratoMsg.texto}
              </p>
            )}
          </div>
        </>
      )}
    />
  );
}

